// Playback survives a page reload.
//
// The queue lives inside the MusicKit instance, which dies with the page — so a refresh
// used to drop the music entirely. We mirror the live queue (song ids + what is playing +
// where in the track) into localStorage and re-queue it on the next load.
//
// Restored PAUSED, on purpose: browsers refuse to start audio without a user gesture on
// the new page, so auto-resuming would either fail silently or fight the browser. The
// banner comes back with the track and position ready, and one click continues.
import type { MusicItem } from '../lib/types';

const KEY = 'kp-music-session';
/** Older than this and it isn't "what I was listening to" any more. */
const MAX_AGE_MS = 12 * 60 * 60 * 1000;
const MAX_TRACKS = 200;

export interface SavedTrack { id: string; title?: string; artist?: string }
export interface SavedSession {
  tracks: SavedTrack[];
  index: number;
  positionMs: number;
  savedAt: number;
}

export function saveSession(s: Omit<SavedSession, 'savedAt'>): void {
  try {
    if (!s.tracks.length) { clearSession(); return; }
    const payload: SavedSession = { ...s, tracks: s.tracks.slice(0, MAX_TRACKS), savedAt: Date.now() };
    localStorage.setItem(KEY, JSON.stringify(payload));
  } catch { /* private mode / quota — playback just won't survive the reload */ }
}

export function loadSession(now: number = Date.now()): SavedSession | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as SavedSession;
    if (!Array.isArray(s?.tracks) || s.tracks.length === 0) return null;
    if (typeof s.savedAt !== 'number' || now - s.savedAt > MAX_AGE_MS) { clearSession(); return null; }
    const index = Math.min(Math.max(0, Number(s.index) || 0), s.tracks.length - 1);
    return { tracks: s.tracks, index, positionMs: Math.max(0, Number(s.positionMs) || 0), savedAt: s.savedAt };
  } catch {
    return null;
  }
}

export function clearSession(): void {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
}

/** The live queue, reduced to what is worth storing. */
export function toSaved(items: MusicItem[]): SavedTrack[] {
  return items.filter((i) => i.kind === 'song' && i.id).map((i) => ({ id: i.id, title: i.title, artist: i.artist }));
}

export const sessionTrackToItem = (t: SavedTrack): MusicItem =>
  ({ kind: 'song', id: t.id, provider: 'apple_music', title: t.title || '', artist: t.artist });
